import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colorTokens, radiusTokens, scrollbarTokens, spacingTokens, typographyTokens } from '../app/designTokens';
import { EmptyState } from './EmptyState';
import { LoadingState } from './LoadingState';

const { semantic } = colorTokens;

/** Shared MUI sx for dark table/data scrollbars (pairs with `.maf-table-scroll` CSS). */
export const mafTableScrollSx = {
  scrollbarWidth: 'thin',
  scrollbarColor: `${scrollbarTokens.thumb} ${scrollbarTokens.track}`,
  '&::-webkit-scrollbar': {
    width: scrollbarTokens.size,
    height: scrollbarTokens.size,
  },
  '&::-webkit-scrollbar-track': {
    background: scrollbarTokens.track,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: scrollbarTokens.thumb,
    borderRadius: `${scrollbarTokens.thumbRadius}px`,
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: scrollbarTokens.thumbHover,
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
};

const TYPE_CONFIG = {
  Default: {
    size: 'small',
    headerLetterSpacing: '0.08em',
    monoBody: false,
    cellPy: 1.5,
  },
  Compact: {
    size: 'small',
    headerLetterSpacing: '0.08em',
    monoBody: false,
    cellPy: 1,
  },
  Audit: {
    size: 'small',
    headerLetterSpacing: '0.08em',
    monoBody: true,
    cellPy: 1.25,
  },
  Technical: {
    size: 'small',
    headerLetterSpacing: '0.08em',
    monoBody: true,
    cellPy: 1.25,
  },
};

/**
 * Shared Data / Table (Figma Base).
 * @param {'Default'|'Compact'|'Audit'|'Technical'} [type='Default']
 * @param {boolean} [embedded] - When true, omit outer card chrome (parent SectionCard provides it)
 */
export function DataTable({
  columns,
  rows,
  loading = false,
  emptyMessage,
  size,
  type = 'Default',
  embedded = false,
  getRowKey = (row) => row.id,
  onRowClick,
  sx,
  className,
}) {
  const { t } = useTranslation('common');
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.Default;
  const tableSize = size || config.size;

  if (loading) {
    return (
      <Box sx={{ px: embedded ? 2.5 : 2, py: 3 }}>
        <LoadingState message={t('loading_data')} />
      </Box>
    );
  }

  const containerSx = embedded
    ? {
        bgcolor: 'transparent',
        backgroundImage: 'none',
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        ...mafTableScrollSx,
        ...sx,
      }
    : {
        bgcolor: semantic.bg.surface,
        backgroundImage: 'none',
        border: `1px solid ${semantic.border.subtle}`,
        borderRadius: `${radiusTokens.card}px`,
        boxShadow: 'none',
        overflow: 'hidden',
        ...mafTableScrollSx,
        ...sx,
      };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      className={['maf-table-scroll', className].filter(Boolean).join(' ')}
      sx={containerSx}
    >
      <Table
        size={tableSize}
        sx={{
          minWidth: 640,
          '& .MuiTableCell-root': {
            px: `${spacingTokens.tableCellPaddingX}px`,
            py: config.cellPy,
            borderBottom: `1px solid ${semantic.border.subtle}`,
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              bgcolor: semantic.bg.input,
              '& .MuiTableCell-head': {
                color: semantic.text.muted,
                fontFamily: typographyTokens.fontFamily,
                fontWeight: typographyTokens.fontWeightSemibold,
                fontSize: 11,
                lineHeight: '16px',
                letterSpacing: config.headerLetterSpacing,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                borderBottom: `1px solid ${semantic.border.default}`,
              },
            }}
          >
            {columns.map((column) => (
              <TableCell key={column.id} align={column.align || 'left'} width={column.width}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={getRowKey(row)}
              hover={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                ...(onRowClick ? { cursor: 'pointer' } : null),
                bgcolor: 'transparent',
                transition: 'background-color 120ms ease',
                '& .MuiTableCell-body': {
                  color: semantic.text.secondary,
                  fontFamily: config.monoBody
                    ? typographyTokens.fontFamilyMono
                    : typographyTokens.fontFamily,
                  fontSize: 13,
                  lineHeight: '20px',
                  verticalAlign: 'middle',
                },
                '&:last-of-type .MuiTableCell-body': {
                  borderBottom: embedded ? 'none' : undefined,
                },
                '&:hover': {
                  bgcolor: semantic.action.secondary.bg,
                },
              }}
            >
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align || 'left'}>
                  {column.render ? column.render(row) : row[column.id]}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ borderBottom: 'none', py: 4 }}>
                <EmptyState message={emptyMessage} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
